import { AuthApiError, FunctionsHttpError } from "@supabase/supabase-js";

import { supabase } from "@/shared/lib/supabaseClient";

export interface LoginCredenciais {
  cpf: string;
  senha: string;
}

/**
 * Camada de acesso a dados para autenticação.
 *
 * Estratégia de login (Fase 4 — Segurança e Autenticação):
 * Toda conta usa o e-mail REAL da pessoa como identidade no Supabase Auth
 * (necessário para o fluxo nativo de recuperação de senha funcionar).
 * Contas criadas antes dessa mudança foram corrigidas diretamente no banco
 * (migration 0012) — não há mais contas usando e-mail técnico, então o
 * login não precisa de fallback nem de migração em tempo de execução.
 *
 * O login continua sendo feito por CPF do ponto de vista do usuário —
 * `buscarEmailReal` traduz CPF -> e-mail real via RPC antes de autenticar.
 */
export const authService = {
  async login({ cpf, senha }: LoginCredenciais) {
    const emailReal = await buscarEmailReal(cpf);

    if (!emailReal) {
      // Não expõe se foi "CPF não existe" vs "senha errada" (evita enumeração de usuários).
      throw new Error("CPF ou senha inválidos.");
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: emailReal,
      password: senha,
    });

    if (error) {
      throw new Error("CPF ou senha inválidos.");
    }

    return data;
  },

  async logout() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error("Não foi possível encerrar a sessão. Tente novamente.");
    }
  },

  async getSessaoAtual() {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      throw new Error("Não foi possível recuperar a sessão.");
    }
    return data.session;
  },

  onAuthStateChange(callback: Parameters<typeof supabase.auth.onAuthStateChange>[0]) {
    return supabase.auth.onAuthStateChange(callback);
  },

  /**
   * Fluxo nativo do Supabase Auth: envia e-mail de redefinição de senha
   * para o e-mail real associado ao CPF informado. Não expõe se o CPF
   * existe ou não — sempre retorna sucesso genérico (evita enumeração).
   *
   * Frente 1 (Sprint 5, 02/08/2026): o erro de `resetPasswordForEmail` era
   * ignorado por completo antes — mesmo um rate limit de envio de e-mail
   * (`over_email_send_rate_limit`) resultava em "sucesso" na tela, sem
   * avisar a pessoa de que nada foi enviado. Esse código é seguro de expor
   * (o limite é por e-mail-alvo, só dispara depois que o CPF já foi
   * resolvido — não ajuda a descobrir CPFs novos, só avisa quem já está
   * repetindo o pedido para a própria conta), então passa a ser tratado.
   * Qualquer outro erro de `resetPasswordForEmail` continua silencioso de
   * propósito, exatamente como o comportamento para "CPF não existe".
   */
  async solicitarRedefinicaoSenha(cpf: string) {
    const emailReal = await buscarEmailReal(cpf);

    // Silenciosamente não faz nada se o CPF não existir — a mensagem na
    // tela é sempre genérica, para não revelar quais CPFs estão cadastrados.
    if (!emailReal) return;

    const { error } = await supabase.auth.resetPasswordForEmail(emailReal, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });

    if (error instanceof AuthApiError && error.code === "over_email_send_rate_limit") {
      throw new Error(
        "Muitos pedidos de redefinição em pouco tempo para esta conta. Aguarde alguns minutos e tente novamente.",
      );
    }
    // Qualquer outro erro (rede, indisponibilidade momentânea do serviço de
    // e-mail etc.) permanece silencioso — mesma mensagem genérica de sucesso.
  },

  /**
   * Define a nova senha após o usuário clicar no link do e-mail de
   * redefinição (fluxo nativo do Supabase Auth — a sessão temporária de
   * recuperação já vem estabelecida pelo próprio link).
   *
   * Frente 1 (Sprint 5, 02/08/2026, BugSenha.docx): antes, qualquer erro
   * aqui caia na mesma mensagem genérica de "link expirado" — inclusive
   * quando o motivo real era a pessoa repetir a senha atual (`same_password`)
   * ou escolher uma senha que não atende à política de força do Supabase
   * Auth (`weak_password`), dando a entender, errado, que o link tinha
   * parado de funcionar. Distingue por `error.code` (suportado pela versão
   * instalada do `@supabase/auth-js`, 2.108.2 — confirmado em
   * package-lock.json) só os dois cenários com causa e mensagem realmente
   * diferentes; qualquer outro código (link expirado de verdade, sessão de
   * recuperação ausente, ou qualquer erro não mapeado) cai no mesmo texto
   * de antes, que é o fallback correto — nunca fica pior que o
   * comportamento anterior.
   */
  async definirNovaSenha(novaSenha: string) {
    const { error } = await supabase.auth.updateUser({ password: novaSenha });
    if (!error) return;

    if (error instanceof AuthApiError) {
      if (error.code === "same_password") {
        throw new Error("A nova senha precisa ser diferente da senha atual.");
      }
      if (error.code === "weak_password") {
        // O Supabase já traz uma mensagem específica (ex.: comprimento
        // mínimo, caracteres exigidos pela política configurada no
        // projeto) — repassar em vez de genericizar.
        throw new Error(error.message || "Escolha uma senha mais forte.");
      }
    }

    throw new Error("Não foi possível redefinir a senha. O link pode ter expirado.");
  },

  /**
   * Fatores de verificação em duas etapas (MFA/TOTP) já cadastrados na
   * PRÓPRIA sessão logada. A API do Supabase Auth só opera sobre o usuário
   * autenticado atual — não existe parâmetro de "outro usuário" em nenhuma
   * dessas chamadas, então não dá pra usar este service pra mexer no fator
   * de MFA de outra pessoa.
   */
  async listarFatoresMfa() {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) {
      throw new Error("Não foi possível verificar o status da verificação em duas etapas.");
    }
    return data.totp;
  },

  /** Nível de garantia de autenticação da sessão atual (aal1 vs aal2). */
  async nivelSessaoMfa() {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) {
      throw new Error("Não foi possível verificar o nível de segurança da sessão.");
    }
    return data;
  },

  /**
   * Inicia o cadastro de um fator TOTP novo, retornando o QR code (SVG) e o
   * código manual pra escanear/digitar no app autenticador. Limpa antes
   * qualquer fator TOTP "não confirmado" de tentativas anteriores abandonadas
   * (ex.: a pessoa fechou a aba no meio do processo), pra não acumular lixo
   * e não esbarrar no limite de fatores por usuário do Supabase Auth.
   */
  async cadastrarFatorMfa() {
    const { data: fatoresAtuais, error: erroListagem } = await supabase.auth.mfa.listFactors();
    if (erroListagem) {
      throw new Error("Não foi possível verificar fatores existentes.");
    }
    // `fatoresAtuais.totp` (agrupado por tipo) só inclui fatores JÁ
    // verificados — a própria implementação do SDK filtra por
    // `status === "verified"` antes de popular esse array. Fatores
    // "unverified" abandonados só aparecem em `fatoresAtuais.all`.
    const naoConfirmados = fatoresAtuais.all.filter(
      (fator) => fator.factor_type === "totp" && fator.status === "unverified",
    );
    for (const fator of naoConfirmados) {
      await supabase.auth.mfa.unenroll({ factorId: fator.id });
    }

    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp" });
    if (error || !data) {
      throw new Error("Não foi possível iniciar a configuração da verificação em duas etapas.");
    }
    return {
      factorId: data.id,
      qrCodeSvg: data.totp.qr_code,
      secret: data.totp.secret,
    };
  },

  /**
   * Confirma um código de 6 dígitos do app autenticador contra um fator já
   * cadastrado (seja o cadastro inicial, seja a confirmação exigida a cada
   * sessão nova) e eleva a sessão atual para aal2 quando bem-sucedido.
   */
  async confirmarCodigoMfa(factorId: string, codigo: string) {
    const { data: desafio, error: erroDesafio } = await supabase.auth.mfa.challenge({ factorId });
    if (erroDesafio || !desafio) {
      throw new Error("Não foi possível iniciar a verificação. Tente novamente.");
    }

    const { error: erroVerificacao } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: desafio.id,
      code: codigo,
    });
    if (erroVerificacao) {
      throw new Error("Código inválido ou expirado. Confira o app autenticador e tente de novo.");
    }
  },
};

/**
 * Busca o e-mail real associado ao CPF.
 *
 * SEG-11 (29/07/2026): antes chamava a RPC `buscar_email_por_cpf` direto do
 * client (anon/authenticated tinham EXECUTE nela, sem nenhum limite de
 * tentativas). Agora passa exclusivamente pela Edge Function `login-por-cpf`
 * (supabase/functions/login-por-cpf/index.ts), que roda com service_role e
 * aplica um rate limit temporário por IP (janela deslizante, sem bloqueio
 * persistente) antes de chamar a mesma RPC no servidor. A migration 0068
 * revogou o EXECUTE de anon/authenticated na RPC — só a Edge Function
 * ainda a acessa.
 *
 * Retorna null tanto se o CPF não existe quanto se a chamada falhar por
 * qualquer motivo que não seja rate limit — preserva o comportamento de
 * não expor se foi "CPF não existe" vs. outra causa (evita enumeração).
 * O rate limit (429) é a única falha tratada à parte: não revela nada
 * sobre o CPF em si (é por IP), então vale avisar o usuário do motivo real
 * em vez de cair na mensagem genérica de credenciais inválidas.
 */
async function buscarEmailReal(cpf: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke<{ email: string | null }>("login-por-cpf", {
    body: { cpf },
  });

  if (!error) return data?.email ?? null;

  if (error instanceof FunctionsHttpError) {
    const corpo = await error.context.json().catch(() => null as { code?: string } | null);
    if (corpo?.code === "muitas_tentativas") {
      throw new Error("Muitas tentativas de login. Aguarde alguns minutos e tente novamente.");
    }
  }

  return null;
}
